import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { PopupListItem, Service } from 'webapp-types';
import { Direction, PopupEvent } from 'webapp/enums/enums';

export interface NavigationCounterItem {
	index: number;
	text: string;
}

interface Args {
	items: NavigationCounterItem[];
	selectedIndex: number;
	onSelect: (index: number) => void;
	theme?: string;
	class?: string;
}

export default class CommonNavigationCounterComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;

	get totalCount() {
		return this.args.items.length;
	}

	get positionText() {
		const itemNumber = this.args.selectedIndex + 1;
		return `${itemNumber} / ${this.totalCount}`;
	}

	get isPopupDisabled() {
		return this.totalCount <= 1;
	}

	get popupItemsList(): PopupListItem<string>[] {
		return this.args.items.map((item) => ({
			text: item.text,
			action: String(item.index),
			isSelected: item.index === this.args.selectedIndex,
		}));
	}

	@action
	openNavigationPopup(node: HTMLElement) {
		if (this.isPopupDisabled) {
			return;
		}

		const popup = this.popupService.createListPopup(
			node,
			this.args.theme || '',
			'navigation-popup',
		);

		if (!popup) {
			return;
		}

		popup.directions = [Direction.UP, Direction.RIGHT, Direction.DOWN, Direction.LEFT];

		popup.on(PopupEvent.OPENED, this, () => {
			this.scrollToSelectedItem();
		});

		popup.open({
			itemsList: this.popupItemsList,
			doAction: (actionName: string) => {
				const index = parseInt(actionName || '0', 10);
				this.args.onSelect(index);
			},
		});

		return popup;
	}

	scrollToSelectedItem() {
		const selectedItem = document.querySelector('.navigation-popup .selected');
		if (selectedItem) {
			selectedItem.scrollIntoView({ block: 'nearest' });
		}
	}

	@action
	handleClick(e: PointerEvent) {
		e.preventDefault();
		e.stopPropagation();
		const node = e.currentTarget as HTMLElement;
		this.openNavigationPopup(node);
	}

	<template>
		<div role="button" class={{@class}} {{on "click" this.handleClick}}>
			{{this.positionText}}
		</div>
	</template>
}
